
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeroProps {
  scrollY: number;
}

export default function Hero({ scrollY }: HeroProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const parallaxOffset = scrollY * 0.3;

  const scrollToShop = () => {
    const element = document.getElementById('shop');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToProducts = () => {
    navigate('/products');
    window.scrollTo(0, 0);
  };

  const navigateToShadowWork = () => {
    navigate('/shadow-work');
    window.scrollTo(0, 0);
  };

  const navigateToVlogs = () => {
    navigate('/vlogs');
    window.scrollTo(0, 0);
  };

  const navigateToReminders = () => {
    navigate('/reminders');
    window.scrollTo(0, 0);
  };

  const navigateToTrapTarot = () => {
    navigate('/trap-tarot');
    window.scrollTo(0, 0);
  };

  const navigateToHopeRitual = () => {
    navigate('/hope-ritual');
    window.scrollTo(0, 0);
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/7cf4a801480ecd12c706961ccbedc21e.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        transform: `translateY(${parallaxOffset}px)`
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
      
      {/* Animated Steam Effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="steam-container">
          <div className="steam steam-1"></div>
          <div className="steam steam-2"></div>
          <div className="steam steam-3"></div>
          <div className="steam steam-4"></div>
          <div className="steam steam-5"></div>
        </div>
      </div>
      
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        {/* Sacred Frequency Title with Glow Effect */}
        <div className={`mb-12 transition-all duration-2000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-6xl md:text-8xl font-bold mb-4 glow-text animate-shimmer bg-gradient-to-r from-amber-200 via-amber-100 to-amber-200 bg-clip-text text-transparent">
            Sacred Frequency
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 font-light tracking-wide">
            Ancient Wisdom • Modern Healing • Sacred Transformation
          </p>
        </div>

        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto transition-all duration-2000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button 
            onClick={scrollToShop}
            className="bg-gradient-to-r from-amber-700 via-red-800 to-red-900 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-amber-800 hover:via-red-900 hover:to-red-950 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Sacred Rituals
          </button>
          <button 
            onClick={navigateToProducts}
            className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Products
          </button>
          <button 
            onClick={navigateToShadowWork}
            className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-purple-700 hover:via-violet-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Shadow Work
          </button>
          <button 
            onClick={navigateToHopeRitual}
            className="bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-rose-700 hover:via-pink-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Hope Ritual
          </button>
          <button 
            onClick={navigateToReminders}
            className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-orange-700 hover:via-red-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Daily Reminders
          </button>
          <button 
            onClick={navigateToTrapTarot}
            className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Trap Tarot
          </button>
          <button 
            onClick={navigateToVlogs}
            className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-4 py-3 rounded-full text-sm font-semibold hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Vlogs
          </button>
          <button 
            onClick={scrollToAbout}
            className="border-2 border-amber-300 text-amber-100 px-4 py-3 rounded-full text-sm font-semibold hover:bg-gradient-to-r hover:from-amber-600 hover:to-red-700 hover:text-white hover:border-transparent transform hover:scale-105 transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Our Story
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-amber-300 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-amber-300 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>

      <style jsx>{`
        .steam-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        .steam {
          position: absolute;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
          border-radius: 50%;
          animation: steamRise 8s infinite ease-in-out;
          opacity: 0;
        }

        .steam-1 {
          width: 60px;
          height: 60px;
          left: 20%;
          animation-delay: 0s;
          animation-duration: 12s;
        }

        .steam-2 {
          width: 40px;
          height: 40px;
          left: 40%;
          animation-delay: 2s;
          animation-duration: 10s;
        }

        .steam-3 {
          width: 80px;
          height: 80px;
          left: 60%;
          animation-delay: 4s;
          animation-duration: 14s;
        }

        .steam-4 {
          width: 50px;
          height: 50px;
          left: 80%;
          animation-delay: 6s;
          animation-duration: 11s;
        }

        .steam-5 {
          width: 70px;
          height: 70px;
          left: 10%;
          animation-delay: 8s;
          animation-duration: 13s;
        }

        @keyframes steamRise {
          0% {
            bottom: -100px;
            opacity: 0;
            transform: translateX(0) scale(0.5);
          }
          10% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.6;
            transform: translateX(20px) scale(1);
          }
          80% {
            opacity: 0.3;
            transform: translateX(-10px) scale(1.2);
          }
          100% {
            bottom: 100vh;
            opacity: 0;
            transform: translateX(30px) scale(0.8);
          }
        }

        .glow-text {
          text-shadow: 
            0 0 20px rgba(251, 191, 36, 0.8),
            0 0 40px rgba(251, 191, 36, 0.6),
            0 0 60px rgba(251, 191, 36, 0.4),
            0 0 80px rgba(251, 191, 36, 0.3);
        }

        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        .animate-shimmer {
          background-size: 200% auto;
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </section>
  );
}